import {
  GetInvalidGroupingSelectOptions,
  PerspectiveGroupingRule,
  ReferenceTypeNameGroupingRule,
} from '../types';
import { TextInput } from '@ardoq/forms';
import { Select } from '@ardoq/select';
import { GroupLabel } from './SharedGroupRuleElements';
import { GroupType, ReferenceDirection } from '@ardoq/api-types';
import { DisabledOptionProps } from '../../types';
import { isTruthy } from '@ardoq/common-helpers';
import { type I18nLocale, t } from '@ardoq/i18n';

const toReferenceTypeOption = (referenceTypeName: string) => ({
  value: referenceTypeName,
  label: referenceTypeName,
});

const getReferenceDirectionOptions = (i18nLocale: I18nLocale) => [
  {
    value: ReferenceDirection.INCOMING,
    label: t(i18nLocale, 'perspectives:groupingRules.incoming', 'Incoming'),
  },
  {
    value: ReferenceDirection.OUTGOING,
    label: t(i18nLocale, 'perspectives:groupingRules.outgoing', 'Outgoing'),
  },
];

type ReferenceTypeNameGroupRuleProps = {
  groupingRule: ReferenceTypeNameGroupingRule;
  referenceTypeNames: string[];
  onGroupingRuleUpdated: (
    groupingRuleId: string,
    updatedGroupingRule: Partial<PerspectiveGroupingRule>
  ) => void;
  tabIndex?: number;
  disabledOptionProps?: DisabledOptionProps;
  getInvalidGroupingSelectOptions?: GetInvalidGroupingSelectOptions;
  i18nLocale: I18nLocale;
};

const ReferenceTypeNameGroupRule = ({
  groupingRule,
  referenceTypeNames,
  onGroupingRuleUpdated,
  getInvalidGroupingSelectOptions,
  tabIndex,
  i18nLocale,
}: ReferenceTypeNameGroupRuleProps) => {
  const { ruleTargetSelectOption } =
    getInvalidGroupingSelectOptions?.(groupingRule) || {};

  const referenceTypeOptions = [
    ruleTargetSelectOption,
    ...referenceTypeNames.map(toReferenceTypeOption),
  ].filter(isTruthy);

  const updateGroupRule = (
    partialGroupingRule: Partial<PerspectiveGroupingRule>
  ) => onGroupingRuleUpdated(groupingRule.id, partialGroupingRule);

  return (
    <>
      <GroupLabel data-testid={`group-by-${GroupType.REFERENCE}-label`}>
        {t(
          i18nLocale,
          'perspectives:groupingRules.referenceTypeNameLabel',
          'Reference Type Name'
        )}
      </GroupLabel>
      <TextInput
        placeholder={t(
          i18nLocale,
          'perspectives:groupingRules.allWorkspacesPlaceholder',
          'All workspaces'
        )}
        isDisabled
      />
      <Select
        dataTestId="reference-type-name-grouping-rule-reference-types-select"
        aria-label={t(
          i18nLocale,
          'perspectives:groupingRules.referenceTypeNamesAriaLabel',
          'Reference type names'
        )}
        options={referenceTypeOptions}
        value={groupingRule.targetId}
        tabIndex={tabIndex}
        onChange={event => {
          updateGroupRule({
            targetId: event?.value,
          });
        }}
      />
      <Select
        aria-label={t(
          i18nLocale,
          'perspectives:groupingRules.referenceDirectionAriaLabel',
          'Reference direction'
        )}
        dataTestId="reference-type-grouping-rule-direction-select"
        value={groupingRule.direction}
        options={getReferenceDirectionOptions(i18nLocale)}
        tabIndex={tabIndex}
        onChange={event =>
          updateGroupRule({
            direction: event?.value ?? ReferenceDirection.INCOMING,
          })
        }
      />
      <TextInput
        value={groupingRule.sublabel}
        dataTestId="reference-type-grouping-rule-sublabel-input"
        placeholder={t(
          i18nLocale,
          'perspectives:groupingRules.addLabelPlaceholder',
          'Add a label'
        )}
        tabIndex={tabIndex}
        onValueChange={value =>
          updateGroupRule({
            sublabel: value,
          })
        }
      />
    </>
  );
};

export { ReferenceTypeNameGroupRule };
